import isEqual from 'lodash-es/isEqual';
import { useRef } from 'react';

interface MemoCache<T> {
  dependencies: unknown[];
  value: T;
}

/**
 * `useMemo`, but dependencies are compared with `isEqual` instead of by identity.
 * Use it when a dependency is an object or array that is rebuilt every render
 * (an inline literal, a `map` result) but whose contents rarely change.
 *
 * The cache is read during render on purpose - a ref is the only place the
 * previous dependencies can live so they can be compared to the current ones -
 * so `react-hooks/refs` is disabled where it fires.
 */
export const useDeepCompareMemo = <T>(
  factory: () => T,
  dependencies: unknown[]
): T => {
  const cache = useRef<MemoCache<T> | null>(null);

  // eslint-disable-next-line react-hooks/refs
  if (!cache.current || !isEqual(cache.current.dependencies, dependencies)) {
    cache.current = { dependencies, value: factory() };
  }

  // eslint-disable-next-line react-hooks/refs
  return cache.current.value;
};
